import { useMemo } from "react";

import GoTo from "@/app/shared/GoTo";
import { selectableColors } from "@/design/colors/colors";
import useNow from "@/utils/hooks/useNow";

import { Box, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { addDays, format, isSameDay, isSameSecond, startOfDay, startOfWeek, subSeconds } from "date-fns";
import PropTypes from "prop-types";

function HolidayCard({ day, holiday, getGoTo }) {
  const now = useNow();
  const currentDay = useMemo(() => addDays(startOfWeek(now, { weekStartsOn: 1 }), day), [now, day]);

  const holidayColor = selectableColors.find((color) => holiday.color === color[500]) || blue;

  const overflow = useMemo(() => getGoTo(holiday.overflow), [getGoTo, holiday.overflow]);

  const dateData = useMemo(() => {
    const startDate = format(holiday.start, "yyyy-MM-dd HH:mm:ss");
    const endDate = format(holiday.end, "yyyy-MM-dd HH:mm:ss");

    // A full day starts with the first second of itself and ends with the first second of the next day
    const isFullDay = isSameSecond(startDate, startOfDay(startDate)) && isSameSecond(endDate, startOfDay(endDate));

    if (isFullDay) {
      return "All day";
    } else if (isSameDay(startDate, endDate) && isSameDay(startDate, subSeconds(endDate, 1))) {
      return `${format(startDate, "H:mm")} - ${format(endDate, "H:mm")}`;
    } else if (isSameDay(startDate, currentDay) && !isSameDay(endDate, currentDay)) {
      return `${format(startDate, "H:mm")} - 0:00`;
    } else if (isSameDay(endDate, currentDay) && !isSameDay(startDate, currentDay)) {
      return `0:00 - ${format(endDate, "H:mm")}`;
    } else {
      return "All day";
    }
  }, [currentDay, holiday.end, holiday.start]);

  return (
    <Box
      sx={(theme) => ({
        bgcolor: holidayColor[50],
        border: `1px solid ${holidayColor[500]}`,
        borderLeft: `3px solid ${holidayColor[500]}`,
        py: 0.25,
        px: 1,
        borderRadius: 1,
        textAlign: "initial",
        display: "flex",
        alignItems: "initial",
        justifyContent: "initial",
        flexDirection: "column",
        ...theme.applyStyles("dark", {
          backgroundColor: holidayColor[900],
        }),
      })}
    >
      <Typography
        noWrap
        sx={{
          fontSize: 12,
        }}
      >
        {dateData}
      </Typography>
      <Typography sx={{ lineHeight: 1.2, mt: -0.25 }} noWrap>
        {holiday.name}
      </Typography>
      <Typography
        sx={(theme) => ({
          lineHeight: 1.2,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          color: holidayColor[900],
          fontSize: 12,
          ...theme.applyStyles("dark", {
            color: holidayColor[200],
          }),
        })}
      >
        <GoTo target={overflow} hideType />
      </Typography>
    </Box>
  );
}

HolidayCard.propTypes = {
  holiday: PropTypes.object,
  getGoTo: PropTypes.func,
  day: PropTypes.number,
};

export default HolidayCard;
